import React from "react";
import PropTypes from "prop-types";
import { Box, Card, CardContent, Typography, Grid } from "@mui/material";
import {
  ArrowUpward,
  ArrowDownward,
  ConfirmationNumberOutlined,
  CheckCircleOutlineOutlined,
  HourglassEmptyOutlined,
  CancelOutlined,
} from "@mui/icons-material";
import CountUp from "react-countup";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const metrics = (totalBookings, initiated, paid, cancelled) => [
  {
    label: "Total Bookings",
    value: totalBookings,
    icon: <ConfirmationNumberOutlined />,
    color: "#E50000",
    bg: "#FFF0F0",
    border: "#FFD0D0",
  },
  {
    label: "Successful",
    value: paid.reduce((a, b) => a + b, 0),
    icon: <CheckCircleOutlineOutlined />,
    color: "#10B981",
    bg: "#F0FDF4",
    border: "#A7F3D0",
  },
  {
    label: "Initiated",
    value: initiated.reduce((a, b) => a + b, 0),
    icon: <HourglassEmptyOutlined />,
    color: "#3B82F6",
    bg: "#EFF6FF",
    border: "#BFDBFE",
  },
  {
    label: "Cancelled",
    value: cancelled.reduce((a, b) => a + b, 0),
    icon: <CancelOutlined />,
    color: "#F59E0B",
    bg: "#FFFBEB",
    border: "#FDE68A",
  },
];

const BookingAnalytics = ({
  totalBookings,
  labels,
  initiated,
  paid,
  cancelled,
  percentageChange,
}) => {
  const chartData = labels.map((label, index) => ({
    label,
    Initiated: initiated[index],
    Paid: paid[index],
    Cancelled: cancelled[index],
  }));

  const isPositive = percentageChange >= 0;
  const cards = metrics(totalBookings, initiated, paid, cancelled);

  return (
    <Box>
      {/* KPI Cards */}
      <Grid container spacing={2} mb={3}>
        {cards.map((m) => (
          <Grid item xs={12} sm={6} md={3} key={m.label}>
            <Card
              sx={{
                p: 0,
                overflow: "hidden",
                border: `1px solid ${m.border}`,
                bgcolor: m.bg,
                boxShadow: "none",
                transition: "transform 0.15s, box-shadow 0.15s",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                },
              }}
            >
              <CardContent sx={{ p: "20px !important" }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1.5,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: m.color,
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      fontSize: "0.7rem",
                    }}
                  >
                    {m.label}
                  </Typography>
                  <Box
                    sx={{
                      width: 34,
                      height: 34,
                      borderRadius: 2,
                      bgcolor: `${m.color}18`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: m.color,
                      "& svg": { fontSize: 18 },
                    }}
                  >
                    {m.icon}
                  </Box>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: m.color,
                    fontSize: "2rem",
                    lineHeight: 1,
                  }}
                >
                  <CountUp end={m.value} duration={1.8} separator="," />
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Chart */}
      <Card>
        <CardContent sx={{ p: "24px !important" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Typography variant="subtitle2" fontWeight={600} color="text.primary">
              Booking Trend
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                px: 1.25,
                py: 0.5,
                borderRadius: 6,
                bgcolor: isPositive ? "success.light" : "error.light",
              }}
            >
              {isPositive ? (
                <ArrowUpward sx={{ fontSize: 14, color: "success.dark" }} />
              ) : (
                <ArrowDownward sx={{ fontSize: 14, color: "error.dark" }} />
              )}
              <Typography
                variant="caption"
                fontWeight={600}
                color={isPositive ? "success.dark" : "error.dark"}
              >
                {Math.abs(percentageChange)}% vs last period
              </Typography>
            </Box>
          </Box>

          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F5" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 12, fill: "#9CA3AF" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 12, fill: "#9CA3AF" }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                contentStyle={{
                  borderRadius: 8,
                  border: "1px solid #F0F0F5",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                  fontSize: 13,
                }}
              />
              <Legend
                wrapperStyle={{ fontSize: 13, paddingTop: 12 }}
              />
              <Line
                type="monotone"
                dataKey="Initiated"
                stroke="#3B82F6"
                strokeWidth={2.5}
                dot={false}
                activeDot={{ r: 5 }}
              />
              <Line
                type="monotone"
                dataKey="Paid"
                stroke="#10B981"
                strokeWidth={2.5}
                dot={false}
                activeDot={{ r: 5 }}
              />
              <Line
                type="monotone"
                dataKey="Cancelled"
                stroke="#F59E0B"
                strokeWidth={2.5}
                dot={false}
                activeDot={{ r: 5 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

BookingAnalytics.propTypes = {
  totalBookings: PropTypes.number.isRequired,
  labels: PropTypes.arrayOf(PropTypes.string).isRequired,
  initiated: PropTypes.arrayOf(PropTypes.number).isRequired,
  paid: PropTypes.arrayOf(PropTypes.number).isRequired,
  cancelled: PropTypes.arrayOf(PropTypes.number).isRequired,
  percentageChange: PropTypes.number,
};

export default BookingAnalytics;
